import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/EditOutlined';
import { ListSkeleton, LoadingButton, PageHeader } from '@routes-plan/ui';
import { AdminPage, useAlert, useConfirm } from '../shared/AdminUi';
import axios from 'axios';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';
import { shouldRenderForbiddenAction } from '../../../utils/permission-ui';
import StandardModal from '../../../components/modales/StandardModal';
import { useT } from '../../../i18n';
import { setPageTitle } from '../../../utils/pageTitle';

interface ShowAlerts {
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
}

type NavigationMenu = {
  id: number;
  name: string;
  route: string;
  order: number;
  icon: string;
  permissions?: {
    id: number;
    name: string;
    action: string;
  }[];
};

type NavigationModule = {
  id: number;
  name: string;
  order: number;
  icon: string;
  visible: boolean;
  menu: NavigationMenu[];
};

type ModuleFormState = {
  id?: number;
  name: string;
  order: string;
  icon: string;
  visible: boolean;
};

type MenuFormState = {
  id?: number;
  name: string;
  route: string;
  order: string;
  icon: string;
  actions: string[];
};

type MenuActionOption = {
  action: string;
  label: string;
};

type Translate = ReturnType<typeof useT>;

// Acciones disponibles por menú; solo se traduce el texto que se muestra (label).
const getMenuActionOptions = (t: Translate): MenuActionOption[] => [
  { action: 'read', label: t('permissions.actionRead') },
  { action: 'create', label: t('common.create') },
  { action: 'update', label: t('common.edit') },
  { action: 'delete', label: t('common.delete') },
  { action: 'assign', label: t('common.assign') },
  { action: 'manage_mobile', label: t('permissions.actionManage') },
  { action: 'delete_box_movement', label: t('permissions.actionDeleteMovement') },
  { action: 'delete_expense_movement', label: t('permissions.actionDeleteMovement') },
  { action: 'show_location', label: t('permissions.actionShowLocation') },
  { action: 'allow_manual_discount', label: t('permissions.actionManualDiscount') },
  { action: 'allow_automatic_discount', label: t('permissions.actionAutomaticDiscount') },
  { action: 'pay', label: t('permissions.actionPay') },
  { action: 'refinance', label: t('permissions.actionRefinance') },
  { action: 'update_constant', label: t('permissions.actionUpdateLoanType') },
  { action: 'delete_selected', label: t('permissions.actionDeleteSelected') },
  { action: 'pdf', label: t('permissions.actionPdf') },
  { action: 'adjust_box', label: t('permissions.actionAdjustBox') },
  { action: 'register_expenses', label: t('permissions.actionRegisterExpenses') },
  { action: 'export', label: t('common.export') },
  { action: 'view_errors', label: t('permissions.actionViewErrors') },
  { action: 'transfer', label: t('permissions.actionTransfer') },
  { action: 'update_quote', label: t('permissions.actionUpdateQuota') },
  { action: 'manage_backup_alert', label: t('permissions.actionManageAlert') },
  { action: 'toggle_enabled', label: t('permissions.actionToggleEnabled') },
] ;

const getMenuActionLabels = (options: MenuActionOption[], t: Translate) =>
  options.reduce<Record<string, string>>((acc, item) => {
    acc[item.action] = item.label;
    return acc;
  }, { toggle_visibility: t('permissions.actionToggleVisibility') });

const dedupePermissionActions = <T extends { action: string }>(permissions: T[] = []) => {
  const seenActions = new Set<string>();
  return permissions.filter((permission) => {
    const action = String(permission.action || '').toLowerCase();
    if (!action || seenActions.has(action)) {
      return false;
    }
    seenActions.add(action);
    return true;
  });
};

const normalizeMenuRoute = (route?: string | null) => {
  const trimmed = String(route || '').trim();
  if (!trimmed) {
    return '';
  }

  const normalized = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return normalized.toLowerCase();
};

const emptyModuleForm: ModuleFormState = {
  name: '',
  order: '',
  icon: 'ViewModuleIcon',
  visible: true,
};

const emptyMenuForm: MenuFormState = {
  name: '',
  route: '',
  order: '',
  icon: 'ViewModuleIcon',
  actions: [],
};

const PermissionNew: React.FC<ShowAlerts> = () => {
  const t = useT();
  const showAlert = useAlert();
  const [confirm, confirmDialog] = useConfirm();
  const [busy, setBusy] = useState(false);
  const showLoading = () => setBusy(true);
  const hideLoading = () => setBusy(false);
  setPageTitle('permissions.manageMenu');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { canCreate, canUpdate, canDelete } = useResourcePermissions('/admin/permissions');
  const showCreateActions = shouldRenderForbiddenAction(canCreate);
  const showUpdateActions = shouldRenderForbiddenAction(canUpdate);
  const showDeleteActions = shouldRenderForbiddenAction(canDelete);

  const [modules, setModules] = useState<NavigationModule[]>([]);
  const [selectedModuleId, setSelectedModuleId] = useState<number | null>(null);
  const [isLoadingModules, setIsLoadingModules] = useState(true);

  const [moduleDialogOpen, setModuleDialogOpen] = useState(false);
  const [moduleForm, setModuleForm] = useState<ModuleFormState>(emptyModuleForm);

  const [menuDialogOpen, setMenuDialogOpen] = useState(false);
  const [menuForm, setMenuForm] = useState<MenuFormState>(emptyMenuForm);

  const selectedModule = useMemo(
    () => modules.find((module) => module.id === selectedModuleId) ?? null,
    [modules, selectedModuleId]
  );
  const menuActionOptions = useMemo(() => getMenuActionOptions(t), [t]);
  const menuActionLabels = useMemo(() => getMenuActionLabels(menuActionOptions, t), [menuActionOptions, t]);
  const availableMenuActionOptions = useMemo(() => {
    const normalizedRoute = normalizeMenuRoute(menuForm.route);
    return menuActionOptions.flatMap((option) => {
      if (option.action === 'read' && normalizedRoute === '/admin/users') {
        return [{ ...option, label: t('permissions.actionViewAssociations') }];
      }
      if (option.action === 'read' && normalizedRoute === '/reports/payments-by-date') {
        return [{ ...option, label: t('permissions.actionShowHistory') }];
      }
      if (option.action === 'create' && normalizedRoute === '/reports/payments-by-date') {
        return [];
      }
      if (option.action === 'create' && normalizedRoute === '/loans/payment-corrections') {
        return [];
      }
      if (option.action === 'read' && normalizedRoute === '/loans/payment-corrections') {
        return [];
      }
      if (option.action === 'assign') {
        if (normalizedRoute === '/admin/loan-types') {
          return [{ ...option, label: t('common.assign') }];
        }
        if (normalizedRoute === '/admin/sales-reps') {
          return [{ ...option, label: t('permissions.actionAssignRoute') }];
        }
        return [];
      }
      if (option.action === 'manage_mobile') {
        return normalizedRoute === '/admin/sales-reps'
          ? [{ ...option, label: t('permissions.actionManageApp') }]
          : [];
      }
      if (option.action === 'delete_box_movement') {
        return normalizedRoute === '/loans/cash-box-expenses/new'
          ? [{ ...option, label: t('permissions.actionDeleteMainBoxMovement') }]
          : [];
      }
      if (option.action === 'delete_expense_movement') {
        return normalizedRoute === '/loans/expenses'
          ? [{ ...option, label: t('permissions.actionDeleteExpenseMovement') }]
          : [];
      }
      if (option.action === 'show_location') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionShowLocation') }]
          : [];
      }
      if (option.action === 'allow_manual_discount') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionManualDiscount') }]
          : [];
      }
      if (option.action === 'allow_automatic_discount') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionAutomaticDiscount') }]
          : [];
      }
      if (option.action === 'pay') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionPay') }]
          : [];
      }
      if (option.action === 'refinance') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionRefinance') }]
          : [];
      }
      if (option.action === 'update_constant') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionUpdateLoanType') }]
          : [];
      }
      if (option.action === 'delete_selected') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionDeleteSelected') }]
          : [];
      }
      if (option.action === 'pdf') {
        return normalizedRoute === '/loans/payments'
          ? [{ ...option, label: t('permissions.actionPdf') }]
          : [];
      }
      if (option.action === 'adjust_box') {
        return normalizedRoute === '/loans/expenses/new'
          ? [{ ...option, label: t('permissions.actionAdjustBox') }]
          : [];
      }
      if (option.action === 'register_expenses') {
        return normalizedRoute === '/loans/expenses/new' || normalizedRoute === '/processes/route-settlement'
          ? [{ ...option, label: t('permissions.actionRegisterExpenses') }]
          : [];
      }
      if (option.action === 'export') {
        return normalizedRoute === '/processes/export'
          ? [{ ...option, label: t('common.export') }]
          : [];
      }
      if (option.action === 'view_errors') {
        return normalizedRoute === '/processes/import'
          ? [{ ...option, label: t('permissions.actionViewErrors') }]
          : [];
      }
      if (option.action === 'transfer') {
        return normalizedRoute === '/processes/loan-transfers'
          ? [{ ...option, label: t('permissions.actionTransfer') }]
          : [];
      }
      if (option.action === 'update_quote') {
        return normalizedRoute === '/admin/clients' || normalizedRoute === '/reports/client-history'
          ? [{ ...option, label: t('permissions.actionUpdateQuota') }]
          : [];
      }
      if (option.action === 'manage_backup_alert') {
        return normalizedRoute === '/admin/clients' || normalizedRoute === '/reports/client-history'
          ? [{ ...option, label: t('permissions.actionManageAlert') }]
          : [];
      }
      if (normalizedRoute === '/admin/clients') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionViewChanges') }];
      }
      if (normalizedRoute === '/reports/loans-by-date') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionHistory') }];
      }
      if (normalizedRoute === '/processes/visit-order') {
        if (option.action === 'update') return [{ ...option, label: t('common.save') }];
        return [];
      }
      if (normalizedRoute === '/processes/route-settlement') {
        if (option.action === 'create') return [{ ...option, label: t('permissions.actionSettle') }];
        if (option.action === 'update') return [{ ...option, label: t('permissions.actionResettle') }];
        if (option.action === 'delete') return [{ ...option, label: t('permissions.actionDeleteExpenses') }];
        if (option.action === 'register_expenses') return [{ ...option, label: t('permissions.actionRegisterExpenses') }];
        return [];
      }
      if (normalizedRoute === '/processes/route-tracking') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionRead') }];
        if (option.action === 'update') return [{ ...option, label: t('permissions.actionUpdateRoute') }];
        return [];
      }
      if (normalizedRoute === '/processes/export') {
        return [];
      }
      if (normalizedRoute === '/processes/import') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionHistory') }];
        if (option.action === 'create') return [{ ...option, label: t('common.save') }];
        if (option.action === 'view_errors') return [{ ...option, label: t('permissions.actionViewErrors') }];
        return [];
      }
      if (normalizedRoute === '/processes/imports') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionRead') }];
        if (option.action === 'delete_selected') return [{ ...option, label: t('permissions.actionDeleteSelected') }];
        if (option.action === 'delete') return [{ ...option, label: t('common.delete') }];
        return [];
      }
      if (normalizedRoute === '/processes/sync-audit') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionRead') }];
        return [];
      }
      if (normalizedRoute === '/processes/loan-transfers') {
        return [];
      }
      if (normalizedRoute === '/loans/route-devices') {
        if (option.action === 'update') return [{ ...option, label: t('common.save') }];
        return [];
      }
      if (normalizedRoute === '/reports/client-history') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionHistory') }];
        if (option.action === 'update_quote') return [{ ...option, label: t('permissions.actionUpdateQuota') }];
        if (option.action === 'manage_backup_alert') return [{ ...option, label: t('permissions.actionManageAlert') }];
        return [];
      }
      if (normalizedRoute === '/reports/expenses') {
        if (option.action === 'delete') return [{ ...option, label: t('common.delete') }];
        return [];
      }
      if (normalizedRoute === '/reports/cash-boxes') {
        if (option.action === 'read') return [{ ...option, label: t('permissions.actionViewBoxDetail') }];
        return [option];
      }
      return [option];
    });
  }, [menuForm.route, menuActionOptions, t]);
  const canSaveModule = moduleForm.id ? canUpdate : canCreate;
  const canSaveMenu = menuForm.id ? canUpdate : canCreate;
  const showModuleSaveAction = shouldRenderForbiddenAction(canSaveModule);
  const showMenuSaveAction = shouldRenderForbiddenAction(canSaveMenu);

  const loadModules = async () => {
    const response = await axios.get('modules/navigation');
    const items: NavigationModule[] = response.data?.data ?? [];
    setModules(items);

    if (items.length === 0) {
      setSelectedModuleId(null);
      return;
    }

    setSelectedModuleId((current) => {
      if (current && items.some((module) => module.id === current)) {
        return current;
      }
      return items[0].id;
    });
  };

  useEffect(() => {
    const bootstrap = async () => {
      try {
        setIsLoadingModules(true);
        await loadModules();
      } catch (error: any) {
        console.error('Error cargando administración de menú', error);
        const message = error?.response?.data?.message || t('permissions.modulesLoadError');
        showAlert(message, 'error');
      } finally {
        setIsLoadingModules(false);
      }
    };

    bootstrap();
  }, [showAlert]);

  const openCreateModuleDialog = () => {
    setModuleForm(emptyModuleForm);
    setModuleDialogOpen(true);
  };

  const openEditModuleDialog = (module: NavigationModule) => {
    setModuleForm({
      id: module.id,
      name: module.name,
      order: String(module.order),
      icon: module.icon,
      visible: Boolean(module.visible),
    });
    setModuleDialogOpen(true);
  };

  const closeModuleDialog = () => {
    setModuleDialogOpen(false);
    setModuleForm(emptyModuleForm);
  };

  const openCreateMenuDialog = () => {
    if (!selectedModuleId) {
      showAlert(t('permissions.selectModuleFirst'), 'warning');
      return;
    }
    setMenuForm(emptyMenuForm);
    setMenuDialogOpen(true);
  };

  const openEditMenuDialog = (menu: NavigationMenu) => {
    setMenuForm({
      id: menu.id,
      name: menu.name,
      route: menu.route,
      order: String(menu.order),
      icon: menu.icon,
      actions: dedupePermissionActions(menu.permissions || [])
        .map((permission) => permission.action)
        .filter((action) => action !== 'toggle_visibility'),
    });
    setMenuDialogOpen(true);
  };

  const closeMenuDialog = () => {
    setMenuDialogOpen(false);
    setMenuForm(emptyMenuForm);
  };

  const submitModule = async () => {
    const payload = {
      name: moduleForm.name.trim(),
      order: moduleForm.order === '' ? undefined : Number(moduleForm.order),
      icon: moduleForm.icon.trim(),
      visible: moduleForm.visible,
    };

    if (!payload.name) {
      showAlert(t('permissions.moduleNameRequired'), 'warning');
      return;
    }

    try {
      showLoading();
      if (moduleForm.id) {
        await axios.put(`modules/${moduleForm.id}`, payload);
        showAlert(t('permissions.moduleUpdatedOk'), 'success');
      } else {
        await axios.post('modules', payload);
        showAlert(t('permissions.moduleCreatedOk'), 'success');
      }
      await loadModules();
      closeModuleDialog();
    } catch (error: any) {
      const message = error?.response?.data?.message || t('permissions.moduleSaveError');
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const submitMenu = async () => {
    if (!selectedModuleId) {
      showAlert(t('permissions.selectModuleFirst'), 'warning');
      return;
    }

    const payload = {
      moduleId: selectedModuleId,
      name: menuForm.name.trim(),
      route: menuForm.route.trim(),
      order: menuForm.order === '' ? undefined : Number(menuForm.order),
      icon: menuForm.icon.trim(),
      actions: menuForm.actions,
    };

    if (!payload.name || !payload.route) {
      showAlert(t('permissions.menuNameRouteRequired'), 'warning');
      return;
    }

    try {
      showLoading();
      if (menuForm.id) {
        await axios.put(`menus/${menuForm.id}`, payload);
        showAlert(t('permissions.menuUpdatedOk'), 'success');
      } else {
        await axios.post('menus', payload);
        showAlert(t('permissions.menuCreatedOk'), 'success');
      }
      await loadModules();
      closeMenuDialog();
    } catch (error: any) {
      const message = error?.response?.data?.message || t('permissions.menuSaveError');
      showAlert(message, 'error');
    } finally {
      hideLoading();
    }
  };

  const toggleMenuAction = (action: string) => {
    setMenuForm((prev) => {
      const nextActions = prev.actions.includes(action)
        ? prev.actions.filter((item) => item !== action)
        : [...prev.actions, action];

      return {
        ...prev,
        actions: nextActions,
      };
    });
  };

  const removeModule = async (moduleId: number) => {
    if (!canDelete) return;

    if (!(await confirm({ title: t('permissions.confirmDeleteModule') }))) return;

    try {
      showLoading();
      await axios.delete(`modules/${moduleId}`);
      showAlert(t('permissions.moduleDeletedOk'), 'success');
      await loadModules();
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || t('permissions.moduleDeleteError');

      if (status === 409) {
        const confirmForce = await confirm({ title: t('permissions.confirmForceDelete'), message });
        if (!confirmForce) {
          hideLoading();
          return;
        }

        try {
          await axios.delete(`modules/${moduleId}`, { params: { force: true } });
          showAlert(t('permissions.moduleForceDeletedOk'), 'success');
          await loadModules();
        } catch (forceError: any) {
          const forceMessage = forceError?.response?.data?.message || t('permissions.forceDeleteError');
          showAlert(forceMessage, 'error');
        }
      } else {
        showAlert(message, 'error');
      }
    } finally {
      hideLoading();
    }
  };

  const removeMenu = async (menuId: number) => {
    if (!canDelete) return;

    if (!(await confirm({ title: t('permissions.confirmDeleteMenu') }))) return;

    try {
      showLoading();
      await axios.delete(`menus/${menuId}`);
      showAlert(t('permissions.menuDeletedOk'), 'success');
      await loadModules();
    } catch (error: any) {
      const status = error?.response?.status;
      const message = error?.response?.data?.message || t('permissions.menuDeleteError');

      if (status === 409) {
        const confirmForce = await confirm({ title: t('permissions.confirmForceDelete'), message });
        if (!confirmForce) {
          hideLoading();
          return;
        }

        try {
          await axios.delete(`menus/${menuId}`, { params: { force: true } });
          showAlert(t('permissions.menuForceDeletedOk'), 'success');
          await loadModules();
        } catch (forceError: any) {
          const forceMessage = forceError?.response?.data?.message || t('permissions.forceDeleteError');
          showAlert(forceMessage, 'error');
        }
      } else {
        showAlert(message, 'error');
      }
    } finally {
      hideLoading();
    }
  };

  return (
    <AdminPage>
      <PageHeader
        title={t('permissions.manageModulesMenus')}
        description={t('permissions.manageModulesMenusHint')}
        breadcrumbs={[{ label: t('permissions.rolesTitle'), href: '#/admin/permissions' }, { label: t('permissions.manageModulesMenus') }]}
        actions={
          showCreateActions ? (
            <Tooltip title={canCreate ? '' : t('common.noPermissionCreate')}>
              <span>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModuleDialog} disabled={!canCreate}>
                  {t('permissions.newModule')}
                </Button>
              </span>
            </Tooltip>
          ) : undefined
        }
      />

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 1fr) minmax(0, 2fr)' }, alignItems: 'start' }}>
        <Box sx={{ minWidth: 0 }}>
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                <Typography variant="h6">{t('permissions.modules')}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('permissions.recordsCount', { count: modules.length })}
                </Typography>
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {isLoadingModules ? (
                <ListSkeleton rows={4} columns={1} />
              ) : modules.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('permissions.noModules')}
                </Typography>
              ) : (
                <List dense sx={{ py: 0, maxHeight: { xs: '32vh', md: '54vh' }, overflowY: 'auto' }}>
                  {modules.map((module) => (
                    <ListItemButton
                      key={module.id}
                      selected={module.id === selectedModuleId}
                      onClick={() => setSelectedModuleId(module.id)}
                      sx={{ borderRadius: 1, mb: 0.5 }}
                    >
                      <ListItemText
                        primary={module.name}
                        secondary={t('permissions.moduleSummary', { order: module.order, count: module.menu?.length ?? 0 })}
                      />
                      {showUpdateActions && (
                        <Tooltip title={canUpdate ? t('permissions.editModule') : t('common.noPermissionEdit')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                openEditModuleDialog(module);
                              }}
                              disabled={!canUpdate}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {showDeleteActions && (
                        <Tooltip title={canDelete ? t('permissions.deleteModule') : t('common.noPermissionDelete')}>
                          <span>
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                removeModule(module.id);
                              }}
                              disabled={!canDelete}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Card variant="outlined">
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Box
                display="flex"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                justifyContent="space-between"
                flexDirection={{ xs: 'column', sm: 'row' }}
                gap={1}
                mb={1}
              >
                <Typography variant="h6">
                  {t('permissions.menus')} {selectedModule ? `· ${selectedModule.name}` : ''}
                </Typography>
                {showCreateActions && (
                  <Tooltip title={canCreate ? t('permissions.createMenu') : t('common.noPermissionCreate')}>
                    <span>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={openCreateMenuDialog}
                        disabled={!canCreate || !selectedModule}
                        fullWidth={isMobile}
                      >
                        {t('permissions.newMenu')}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              {isLoadingModules ? (
                <ListSkeleton rows={4} columns={1} />
              ) : !selectedModule ? (
                <Typography variant="body2" color="text.secondary">
                  {t('permissions.selectModuleHint')}
                </Typography>
              ) : selectedModule.menu?.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('permissions.moduleHasNoMenus')}
                </Typography>
              ) : isMobile ? (
                <Stack spacing={1.25}>
                  {selectedModule.menu.map((menu) => (
                    <Box
                      key={menu.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1.5,
                        p: 1.25,
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        {menu.name}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {menu.route}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t('permissions.menuOrderIcon', { order: menu.order, icon: menu.icon })}
                      </Typography>
                      <Typography variant="caption" display="block" color="text.secondary">
                        {t('permissions.menuActionsLine', {
                          actions: dedupePermissionActions(menu.permissions || [])
                            .map((permission) => menuActionLabels[permission.action] || permission.name)
                            .join(', ') || t('permissions.actionToggleVisibility'),
                        })}
                      </Typography>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end" mt={0.5}>
                        {showUpdateActions && (
                          <Tooltip title={canUpdate ? t('permissions.editMenu') : t('common.noPermissionEdit')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => openEditMenuDialog(menu)}
                                disabled={!canUpdate}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {showDeleteActions && (
                          <Tooltip title={canDelete ? t('permissions.deleteMenu') : t('common.noPermissionDelete')}>
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => removeMenu(menu.id)}
                                disabled={!canDelete}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              ) : (
                <List dense sx={{ py: 0 }}>
                  {selectedModule.menu.map((menu) => (
                    <ListItemButton
                      key={menu.id}
                      disableRipple
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': { backgroundColor: 'action.hover' },
                        cursor: 'default',
                      }}
                    >
                      <ListItemText
                        primary={menu.name}
                        secondary={t('permissions.menuSummary', {
                          route: menu.route,
                          order: menu.order,
                          icon: menu.icon,
                          actions: dedupePermissionActions(menu.permissions || [])
                            .map((permission) => menuActionLabels[permission.action] || permission.name)
                            .join(', ') || t('permissions.actionToggleVisibility'),
                        })}
                      />
                      {showUpdateActions && (
                        <Tooltip title={canUpdate ? t('permissions.editMenu') : t('common.noPermissionEdit')}>
                          <span>
                            <IconButton size="small" onClick={() => openEditMenuDialog(menu)} disabled={!canUpdate}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                      {showDeleteActions && (
                        <Tooltip title={canDelete ? t('permissions.deleteMenu') : t('common.noPermissionDelete')}>
                          <span>
                            <IconButton size="small" onClick={() => removeMenu(menu.id)} disabled={!canDelete}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </ListItemButton>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      <StandardModal
        open={moduleDialogOpen}
        onClose={closeModuleDialog}
        title={moduleForm.id ? t('permissions.editModule') : t('permissions.newModule')}
        maxWidth={600}
        actions={
          <>
            <Button variant="outlined" color="inherit" onClick={closeModuleDialog} disabled={busy}>{t('common.cancel')}</Button>
            {showModuleSaveAction && (
              <LoadingButton onClick={submitModule} variant="contained" disabled={!canSaveModule} loading={busy}>
                {t('common.save')}
              </LoadingButton>
            )}
          </>
        }
      >
        <Stack spacing={2} mt={0.5}>
          <TextField
            label={t('common.name')}
            value={moduleForm.name}
            onChange={(event) => setModuleForm((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            autoFocus
          />
          <TextField
            label={t('permissions.order')}
            type="number"
            value={moduleForm.order}
            onChange={(event) => setModuleForm((prev) => ({ ...prev, order: event.target.value }))}
            fullWidth
          />
          <TextField
            label={t('permissions.icon')}
            value={moduleForm.icon}
            onChange={(event) => setModuleForm((prev) => ({ ...prev, icon: event.target.value }))}
            fullWidth
            helperText={t('permissions.exampleHint', { value: 'ViewModuleIcon' })}
          />
          <FormControlLabel
            control={
              <Switch
                checked={moduleForm.visible}
                onChange={(event) => setModuleForm((prev) => ({ ...prev, visible: event.target.checked }))}
              />
            }
            label={t('permissions.visible')}
          />
        </Stack>
      </StandardModal>

      <StandardModal
        open={menuDialogOpen}
        onClose={closeMenuDialog}
        title={menuForm.id ? t('permissions.editMenu') : t('permissions.newMenu')}
        maxWidth={600}
        actions={
          <>
            <Button variant="outlined" color="inherit" onClick={closeMenuDialog} disabled={busy}>{t('common.cancel')}</Button>
            {showMenuSaveAction && (
              <LoadingButton onClick={submitMenu} variant="contained" disabled={!canSaveMenu} loading={busy}>
                {t('common.save')}
              </LoadingButton>
            )}
          </>
        }
      >
        <Stack spacing={2} mt={0.5}>
          <TextField
            label={t('common.name')}
            value={menuForm.name}
            onChange={(event) => setMenuForm((prev) => ({ ...prev, name: event.target.value }))}
            fullWidth
            autoFocus
          />
          <TextField
            label={t('permissions.path')}
            value={menuForm.route}
            onChange={(event) => setMenuForm((prev) => ({ ...prev, route: event.target.value }))}
            fullWidth
            helperText={t('permissions.exampleHint', { value: '/admin/permissions' })}
          />
          <TextField
            label={t('permissions.order')}
            type="number"
            value={menuForm.order}
            onChange={(event) => setMenuForm((prev) => ({ ...prev, order: event.target.value }))}
            fullWidth
          />
          <TextField
            label={t('permissions.icon')}
            value={menuForm.icon}
            onChange={(event) => setMenuForm((prev) => ({ ...prev, icon: event.target.value }))}
            fullWidth
            helperText={t('permissions.exampleHint', { value: 'ViewModuleIcon' })}
          />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('permissions.menuActions')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t('permissions.menuActionsHint')}
            </Typography>
            <FormGroup sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, columnGap: 2 }}>
              {availableMenuActionOptions.map((option) => (
                <FormControlLabel
                  key={option.action}
                  control={
                    <Checkbox
                      checked={menuForm.actions.includes(option.action)}
                      onChange={() => toggleMenuAction(option.action)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
          </Box>
        </Stack>
      </StandardModal>
      {confirmDialog}
    </AdminPage>
  );
};

export default PermissionNew;
